import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, ChevronLeft, ChevronRight, Link2, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import BookableCalendarsPanel from "@/components/BookableCalendarsPanel";
import ReminderAnalytics from "@/components/ReminderAnalytics";
import GoogleCalendarConnect from "@/components/GoogleCalendarConnect";

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }
function fmt(d) { return d.toISOString().split("T")[0]; }

export default function CalendarPage() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", start_date: "", start_time: "09:00", duration: 30, contact_id: "", notes: "" });

  const load = async () => {
    const { data } = await api.get("/appointments");
    setAppts(data);
  };
  useEffect(() => { load(); api.get("/contacts").then(({ data }) => setContacts(data)); }, []);

  const monthDays = useMemo(() => {
    const first = startOfMonth(cursor);
    const total = daysInMonth(cursor);
    const startWeekday = first.getDay(); // 0=Sun
    const days = [];
    for (let i = 0; i < startWeekday; i++) days.push(null);
    for (let i = 1; i <= total; i++) days.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
    return days;
  }, [cursor]);

  const apptsForDay = (d) => {
    if (!d) return [];
    const key = fmt(d);
    return appts.filter((a) => (a.start || "").startsWith(key)).sort((a, b) => a.start.localeCompare(b.start));
  };

  const monthLabel = cursor.toLocaleString("en-US", { month: "long", year: "numeric" });

  const openCreate = (d) => {
    setForm({
      title: "",
      start_date: fmt(d || new Date()),
      start_time: "09:00",
      duration: 30,
      contact_id: "",
      notes: "",
    });
    setCreating(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const start = new Date(`${form.start_date}T${form.start_time}:00`);
    const end = new Date(start.getTime() + Number(form.duration) * 60000);
    const contact = contacts.find((c) => c.id === form.contact_id);
    const payload = {
      title: form.title,
      start: start.toISOString().replace("Z", "+00:00"),
      end: end.toISOString().replace("Z", "+00:00"),
      contact_id: form.contact_id || null,
      contact_name: contact ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim() : "",
      contact_email: contact ? contact.email : "",
      notes: form.notes,
    };
    try {
      await api.post("/appointments", payload);
      setCreating(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this appointment?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      setAppts((prev) => prev.filter((a) => a.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const publicUrl = `${window.location.origin}/book/${encodeURIComponent(user?.email || "")}`;
  const copyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    toast.success("Public booking link copied");
  };

  return (
    <div className="p-6 md:p-10" data-testid="calendar-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="section-label">Time</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-1">Calendar</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="copy-booking-link" onClick={copyLink} className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase tracking-widest px-4 py-2 text-xs inline-flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Copy legacy link
          </button>
          <button data-testid="new-appt-btn" onClick={() => openCreate(new Date())} className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-4 py-2 text-xs inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New appointment
          </button>
        </div>
      </div>

      <GoogleCalendarConnect />

      <ReminderAnalytics />

      <BookableCalendarsPanel />

      <div className="flex items-center justify-between mb-4">
        <div className="font-heading font-black text-2xl uppercase tracking-tighter">{monthLabel}</div>
        <div className="flex items-center gap-1">
          <button data-testid="cal-prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="w-8 h-8 border border-[var(--peak-ink)] hover:bg-black hover:text-white flex items-center justify-center">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button data-testid="cal-today" onClick={() => setCursor(startOfMonth(new Date()))} className="px-3 h-8 border border-[var(--peak-ink)] hover:bg-black hover:text-white text-xs font-bold uppercase tracking-widest">Today</button>
          <button data-testid="cal-next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="w-8 h-8 border border-[var(--peak-ink)] hover:bg-black hover:text-white flex items-center justify-center">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border border-[var(--peak-border)] bg-white" data-testid="calendar-grid">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="p-2 section-label border-b border-[var(--peak-border)] bg-[var(--peak-surface-alt)]">{d}</div>
        ))}
        {monthDays.map((d, i) => {
          const isToday = d && fmt(d) === fmt(new Date());
          const dayAppts = apptsForDay(d);
          return (
            <div
              key={i}
              className={`min-h-[110px] p-2 border-t border-l border-[var(--peak-border)] ${d ? "cursor-pointer hover:bg-[var(--peak-surface-alt)]" : "bg-[var(--peak-bg)]"}`}
              onClick={() => d && setSelectedDay(d)}
              data-testid={d ? `day-${fmt(d)}` : undefined}
            >
              {d && (
                <>
                  <div className={`flex items-center justify-between ${isToday ? "text-[var(--peak-accent)]" : ""}`}>
                    <span className="font-heading font-bold text-sm">{d.getDate()}</span>
                    {dayAppts.length > 0 && <span className="text-[10px] font-mono">{dayAppts.length}</span>}
                  </div>
                  <div className="mt-1 space-y-1">
                    {dayAppts.slice(0, 3).map((a) => (
                      <div key={a.id} className="truncate text-[11px] px-1 bg-[var(--peak-ink)] text-white">
                        {new Date(a.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {a.title}
                      </div>
                    ))}
                    {dayAppts.length > 3 && <div className="text-[10px] section-label">+{dayAppts.length - 3} more</div>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected day details */}
      {selectedDay && (
        <div className="brutalist-card p-6 mt-6" data-testid="selected-day-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl tracking-tight">
              {selectedDay.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </h2>
            <div className="flex items-center gap-2">
              <button onClick={() => openCreate(selectedDay)} data-testid="add-on-day" className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest text-xs px-3 py-1.5 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
              <button onClick={() => setSelectedDay(null)} className="border border-[var(--peak-ink)] px-2 py-1.5 hover:bg-black hover:text-white"><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          {apptsForDay(selectedDay).length === 0 && <div className="text-sm text-[var(--peak-muted)]">Nothing scheduled.</div>}
          <div className="space-y-2">
            {apptsForDay(selectedDay).map((a) => (
              <div key={a.id} className="border-l-2 border-[var(--peak-accent)] pl-3 py-2 flex items-start justify-between gap-2" data-testid={`appt-row-${a.id}`}>
                <div>
                  <div className="section-label">{new Date(a.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} → {new Date(a.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  <div className="font-semibold text-sm">{a.title}</div>
                  {a.contact_name && <div className="text-xs text-[var(--peak-muted)]">{a.contact_name} · {a.contact_email}</div>}
                  {a.notes && <div className="text-xs mt-1">{a.notes}</div>}
                </div>
                <button onClick={() => remove(a.id)} data-testid={`del-appt-${a.id}`} className="hover:text-[#E50000]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-white border-2 border-[var(--peak-ink)] rounded-none" data-testid="new-appt-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tighter uppercase">New appointment</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="section-label">Title</label>
              <input required data-testid="appt-title" className="brutalist-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="section-label">Date</label>
                <input type="date" required data-testid="appt-date" className="brutalist-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <label className="section-label">Time</label>
                <input type="time" required data-testid="appt-time" className="brutalist-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              </div>
              <div>
                <label className="section-label">Duration (min)</label>
                <input type="number" required data-testid="appt-duration" className="brutalist-input" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="section-label">Contact (optional)</label>
              <select data-testid="appt-contact" className="brutalist-input" value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })}>
                <option value="">—</option>
                {contacts.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} · {c.email}</option>)}
              </select>
            </div>
            <div>
              <label className="section-label">Notes</label>
              <textarea rows={2} data-testid="appt-notes" className="w-full border border-[var(--peak-border)] p-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter className="flex gap-2 sm:justify-end">
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 border-2 border-[var(--peak-ink)] font-bold uppercase text-xs tracking-widest hover:bg-black hover:text-white">Cancel</button>
              <button type="submit" data-testid="submit-appt" className="px-6 py-2 bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest">Create</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
